import { type BitmapText, type Graphics, Rectangle } from "pixi.js";
import { memo, useCallback, useMemo, useRef } from "react";
import { useConstantScreenSize } from "../hooks/useConstantScreenSize";
import { useMapObjectInteractions } from "../hooks/useMapObjectInteractions";
import { useScene } from "../hooks/useScene";
import { useVisibilityWhenFits } from "../hooks/useVisibilityWhenFits";
import type {
    AreaHighlightStyle,
    AreaMapObject,
    LayerId,
    MapItemHandler,
} from "../types";

// Selection/highlight are rendered separately from the base area objects to keep
// controlled visual state from invalidating the base collection. For areas this
// may be more optimization than strictly necessary, but keeping the composition
// consistent with other layers also gives explicit control over render order.
interface AreaObjectProps {
    item: AreaMapObject;
    layerId: LayerId;
    onItemClick?: MapItemHandler<AreaMapObject>;
    onItemHover?: MapItemHandler<AreaMapObject>;
    onItemLeave?: MapItemHandler<AreaMapObject>;
}

const getAreaColor = (hovered: boolean) => {
    if (hovered) return { color: 0xffa500, alpha: 0.5 };
    return { color: 0xffffff, alpha: 0 };
};

function useAreaGeometry(item: AreaMapObject) {
    const { coordinates } = useScene();
    const areaPixi = useMemo(
        () =>
            coordinates.worldRectToScene({
                x: item.x,
                y: item.y,
                width: item.width,
                height: item.height,
            }),
        [coordinates, item.x, item.y, item.width, item.height],
    );
    const bounds = useMemo(
        () =>
            new Rectangle(
                areaPixi.x,
                areaPixi.y,
                areaPixi.width,
                areaPixi.height,
            ),
        [areaPixi],
    );

    return { areaPixi, bounds };
}

const AreaObject = ({
    item,
    layerId,
    onItemClick,
    onItemHover,
    onItemLeave,
}: AreaObjectProps) => {
    const graphicRef = useRef<Graphics | null>(null);
    const { areaPixi, bounds } = useAreaGeometry(item);

    const interaction = useMapObjectInteractions({
        item,
        layerId,
        onItemClick,
        onItemHover,
        onItemLeave,
    });

    const draw = useCallback(
        (graphics: Graphics) => {
            graphicRef.current = graphics;
            graphics.clear();
            graphics.rect(
                areaPixi.x,
                areaPixi.y,
                areaPixi.width,
                areaPixi.height,
            );
            graphics.fill(getAreaColor(interaction.hovered));
            graphics.stroke({ color: 0x000000, pixelLine: true });
        },
        [areaPixi, interaction.hovered],
    );

    return (
        <pixiContainer
            label="area-culling-unit"
            cullable
            cullableChildren={false}
            cullArea={bounds}
        >
            <pixiGraphics
                ref={graphicRef}
                eventMode={interaction.eventMode}
                cursor={interaction.cursor}
                hitArea={bounds}
                onPointerOver={interaction.onPointerOver}
                onPointerOut={interaction.onPointerOut}
                onPointerTap={interaction.onPointerTap}
                draw={draw}
            />
        </pixiContainer>
    );
};

export const AreaLabel = memo(({ item }: { item: AreaMapObject }) => {
    const { labelFontReady } = useScene();

    if (!item.label || !labelFontReady) return null;

    return <LoadedAreaLabel item={item} />;
});

const LoadedAreaLabel = ({ item }: { item: AreaMapObject }) => {
    const textRef = useRef<BitmapText | null>(null);
    const { areaPixi, bounds } = useAreaGeometry(item);

    useConstantScreenSize(textRef, {
        minScale: 0.1,
        maxScale: 0.5,
        allowShrinkToFit: true,
    });
    useVisibilityWhenFits(textRef, bounds);

    return (
        <pixiContainer
            label="area-label-culling-unit"
            cullable
            cullableChildren={false}
            cullArea={bounds}
            eventMode="none"
        >
            <pixiBitmapText
                ref={textRef}
                eventMode="none"
                text={item.label}
                anchor={{ x: 0.5, y: 0.5 }}
                x={areaPixi.x + areaPixi.width / 2}
                y={areaPixi.y + areaPixi.height / 2}
                visible={false}
                style={{
                    fontFamily: "MavenPro-Bold",
                    fontSize: 18,
                    fill: "#000000",
                    align: "center",
                }}
            />
        </pixiContainer>
    );
};

AreaLabel.displayName = "AreaLabel";

const AreaOverlay = ({
    item,
    color,
    alpha,
}: {
    item: AreaMapObject;
    color: number;
    alpha: number;
}) => {
    const { areaPixi } = useAreaGeometry(item);
    const draw = useCallback(
        (graphics: Graphics) => {
            graphics.clear();
            graphics.rect(
                areaPixi.x,
                areaPixi.y,
                areaPixi.width,
                areaPixi.height,
            );
            graphics.fill({ color, alpha });
            graphics.stroke({ color: 0x000000, pixelLine: true });
        },
        [areaPixi, color, alpha],
    );

    return <pixiGraphics eventMode="none" draw={draw} />;
};

export const AreaHighlightOverlay = memo(
    ({ item, style }: { item: AreaMapObject; style: AreaHighlightStyle }) => (
        <AreaOverlay
            item={item}
            color={style.color}
            alpha={style.alpha ?? 0.5}
        />
    ),
);

AreaHighlightOverlay.displayName = "AreaHighlightOverlay";

export const AreaSelectionOverlay = memo(
    ({ item }: { item: AreaMapObject }) => (
        <AreaOverlay item={item} color={0xffa500} alpha={0.8} />
    ),
);

AreaSelectionOverlay.displayName = "AreaSelectionOverlay";

export default memo(AreaObject, (prev, next) => {
    return (
        prev.item === next.item &&
        prev.layerId === next.layerId &&
        prev.onItemClick === next.onItemClick &&
        prev.onItemHover === next.onItemHover &&
        prev.onItemLeave === next.onItemLeave
    );
});
